// hooks/useBulkSubmissionProcess.ts
import { useState, useCallback, useMemo } from "react";
import { Socket } from "socket.io-client";
import { ParseResult } from "papaparse"; // Needed for processCsvData
import SocketService from "@services/SocketService"; // Adjust path

// Import DTOs, Models, Enums etc.
import IBulkOrderSubmitStartDTO from "@models/DTO/IBulkOrderSubmitStartDTO"; // Adjust path
import IBulkOrderSubmitProgressDTO from "@models/DTO/IBulkOrderSubmitProgressDTO"; // Adjust path
import IOnBulkOrderSubmitDTO from "@models/DTO/IOnBulkOrderSubmitDTO"; // Adjust path
import IErrorDTO from "@models/DTO/IErrorDTO"; // Adjust path
import IBulkOrderSubmitDTO from "@models/DTO/IBulkOrderSubmitDTO"; // Adjust path
import IOrder_Batch from "@models/IOrder_Batch"; // Adjust path
import ISupplierModel from "@models/ISupplierModel"; // Adjust path
import IUserUI from "@models/IUserUI"; // Adjust path
import IBulkOrderShipDataRowUI from "@models/IBulkOrderShipDataRowUI"; // Adjust path
import IBulkOrderShipDataRow from "@models/IBulkOrderShipDataRow"; // Adjust path
import EndpointsEnum from "@models/EndpointsEnum"; // Adjust path

interface UseBulkSubmissionProcessProps {
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    orderGuids: string[];
    orderBatch?: IOrder_Batch;
    csvData?: ParseResult<any>; // Need access to CSV data from useCsvReaderLogic
    // Need selected Shipstation options if they are part of the payload logic (currently commented out)
    // shipstationCarrier: string;
    // shipstationServiceType: string;
    // shipstationPackageType: string;
    currentTotal: number; // Need the total number of items from the validated list
}

const useBulkOrderSubmitProcess = ({
    socket,
    userModel,
    supplierModel,
    orderGuids,
    orderBatch,
    csvData,
    // shipstationCarrier,
    // shipstationServiceType,
    // shipstationPackageType,
    currentTotal, // Receive currentTotal as a prop
}: UseBulkSubmissionProcessProps) => {
    const [submitActive, setSubmitActive] = useState(false);
    const [totalProcessed, setTotalProcessed] = useState<number | undefined>(undefined);
    const [totalErrors, setTotalErrors] = useState<number | undefined>(undefined);
    const [errorDetails, setErrorDetails] = useState<{ message: string }[] | undefined>(undefined);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [progress, setProgress] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [shipmentBatchNo, setShipmentBatchNo] = useState(0);
    const [shipmentBatchToken, setShipmentBatchToken] = useState("");

    // Submission related form state (options)
    // Logic for initial autoCreatePackages value from the class constructor
    const initialAutoCreatePackages = supplierModel.Supplier_Integration.has_shipstation || false;
    const autoCreatePackagesSetting = supplierModel.settings?.screens?.orders?.bulk_auto_create_shipments;

    const hasShippingIntegration = useMemo(() => {
        return supplierModel.Supplier_Integration.has_shipstation || false;
    }, [supplierModel]);

    const [autoCreatePackages, setAutoCreatePackages] = useState(
        autoCreatePackagesSetting !== undefined ? autoCreatePackagesSetting : initialAutoCreatePackages
    );
    const [advanceToShipped, setAdvanceToShipped] = useState(true);
    const [updateShipstationOrderAddress, setUpdateShipstationOrderAddress] = useState(false);
    const [shipstation_bulk_update_accept, setShipstationBulkUpdateAccept] = useState("");
    const [defaultWeight, setDefaultWeight] = useState("10");

    // Define socket event handlers managed by this hook
    const onBulkOrderSubmit = useCallback((data: IOnBulkOrderSubmitDTO) => {
        setCompleted(true);
        setSubmitActive(false);
        setTotalProcessed(data.totalProcessed);
        setShipmentBatchNo(data.shipmentBatchNo);
        setShipmentBatchToken(data.shipmentBatchToken);
        setTotalErrors(data.totalErrors);
        setErrorDetails(data.errorDetails);
    }, []); // Dependencies: Setters are stable

    const bulkOrderSubmitStart = useCallback((data: IBulkOrderSubmitStartDTO) => {
        setTotalProcessed(0);
        setCurrentProgress(0);
        setCompleted(false);
        setProgress(0);
        // Use the total items from the start data if available,
        // or rely on currentTotal passed as a prop if start isn't guaranteed first
        // For this hook, relying on `currentTotal` prop from the main component is safer
        // since the main component gets the validated order count first.
    }, []); // Dependencies: Setters are stable

    const bulkOrderSubmitProgress = useCallback(
        (data: IBulkOrderSubmitProgressDTO) => {
            // Calculate progress using the total items received at the start or the prop
            const totalItems = data.totalItems > 0 ? data.totalItems : currentTotal; // Use data.totalItems if provided, fallback to prop
            const progress = (Number(data.totalProcessed) / Number(totalItems)) * 100;

            setCurrentProgress(data.totalProcessed);
            setProgress(progress);
        },
        [currentTotal]
    ); // Dependency: currentTotal prop is needed for calculation

    const processCsvData = useCallback((): string => {
        // Uses csvData prop
        const rows = csvData?.data.map((dataRow: IBulkOrderShipDataRowUI) => {
            const rowDetail: IBulkOrderShipDataRow = {
                order: dataRow.Order,
                weight: dataRow.Weight,
                width: dataRow.Width,
                height: dataRow.Height,
                length: dataRow.Length,
                guid: dataRow.Guid,
            };
            return rowDetail;
        });
        return JSON.stringify(rows);
    }, [csvData]); // Dependency: csvData prop

    const submitBulkOrders = useCallback(() => {
        const payload: IBulkOrderSubmitDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuids: orderGuids, // Use prop
            autoCreatePackages: autoCreatePackages, // Use state from this hook
            advanceToShipped: advanceToShipped, // Use state from this hook
        };

        if (shipstation_bulk_update_accept === "YES") {
            // Use state from this hook
            payload.updateShipstationOrderAddress = updateShipstationOrderAddress; // Use state from this hook
        }

        if (orderBatch) {
            // Use prop
            payload.orderBatchGuid = orderBatch.guid;
        }

        if (autoCreatePackages) {
            // Use state from this hook
            // The commented-out defaults are from the original class component
            // payload.defaultServiceType = shipstationServiceType; // Need these from the main component or another hook
            // payload.defaultPackageType = shipstationPackageType;
            // payload.defaultCarrier = shipstationCarrier;
            payload.defaultWeight = defaultWeight; // Use state from this hook
            payload.shipData = processCsvData(); // Call helper function (uses csvData prop)
        }

        socket.emit(EndpointsEnum.OrderEntryBulkOrderSubmit, payload); // Use prop

        setSubmitActive(true); // Use setter from this hook
    }, [
        socket,
        userModel,
        supplierModel,
        orderGuids,
        autoCreatePackages,
        shipstation_bulk_update_accept,
        updateShipstationOrderAddress,
        orderBatch,
        defaultWeight,
        processCsvData, // Dependency as it's a useCallback function
        // Include Shipstation options if uncommented in payload
        // shipstationCarrier,
        // shipstationServiceType,
        // shipstationPackageType,
    ]); // Dependencies

    const getShipmentBatchUrl = useCallback(
        (noPackingList?: boolean): string => {
            const endpointUrl = SocketService.getEndpointUrl();
            let url = `${endpointUrl}/shipstation/batch/labelpack/${shipmentBatchNo}?token=${shipmentBatchToken}`; // Use state from this hook
            if (noPackingList) {
                url += `&inclPackingList=false`;
            }
            return url;
        },
        [shipmentBatchNo, shipmentBatchToken]
    ); // Dependencies: state variables used

    const downloadErrorCsv = useCallback(() => {
        // Use state from this hook
        if (errorDetails) {
            const headerRow = ["Detail"];
            const dataRows: string[][] = errorDetails.map((detail) => {
                return [detail.message];
            });
            const rows = [headerRow, ...dataRows];
            const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "orderwerks-bulk-order-submit-error.csv");
            link.click();
        }
    }, [errorDetails]); // Dependency: errorDetails state

    return {
        submitActive,
        setSubmitActive,
        totalProcessed,
        totalErrors,
        errorDetails,
        currentProgress,
        // currentTotal, // This is now received as a prop
        progress,
        completed,
        shipmentBatchNo,
        shipmentBatchToken,
        autoCreatePackages,
        setAutoCreatePackages, // Expose setter for handleChange
        updateShipstationOrderAddress,
        setUpdateShipstationOrderAddress, // Expose setter for handleChange
        shipstation_bulk_update_accept,
        setShipstationBulkUpdateAccept, // Expose setter for handleChange
        defaultWeight,
        setDefaultWeight, // Expose setter for handleChange
        submitBulkOrders, // Expose submit function
        getShipmentBatchUrl, // Expose helper function
        downloadErrorCsv, // Expose helper function
        // Expose handlers needed by useSocketListeners
        onBulkOrderSubmit,
        bulkOrderSubmitStart,
        bulkOrderSubmitProgress,
        advanceToShipped,
        setAdvanceToShipped, // Expose setter for handleChange
        hasShippingIntegration,
    };
};

export default useBulkOrderSubmitProcess;
